import { FormattedMessage, useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'

import IconButton from 'LuxKit/Button/IconButton'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import Sheet from 'components/utils/Sheet'

const Banner = styled(Sheet)`
  flex-shrink: 0;
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  gap: ${rem(8)};
`

const Message = styled(BodyText)`
  min-width: 0;
`

interface Props {
  onCreateNewChat: () => void
  onDismiss: () => void
}

/**
 * Warning banner shown to a spoofed staff session that has opened a chat the
 * customer CAN see (created by the customer). Warns that anything sent here is
 * visible to the customer and offers to start a new, hidden chat instead.
 * Presentational — the parent decides visibility and supplies both callbacks.
 */
function CustomerVisibleChatBanner({ onCreateNewChat, onDismiss }: Props) {
  const intl = useIntl()

  const closeLabel = intl.formatMessage({
    id: 'WYXh_8yLMKajGJmG4XY0',
    defaultMessage: 'close',
    description: 'Aria label for close button in modal header dialog window',
  })

  return (
    <Banner
      className="no-print"
      background="critical"
      paletteType="inverse"
      padding={8}
    >
      <Message variant="medium" weight="bold" colour="normal-contrast" align="center">
        <FormattedMessage
          id="dHpIzkluSmOu9NOrGKkO"
          defaultMessage="Warning: the customer will view any messages or modifications made to this chat"
          description="Warning banner shown to spoofed staff viewing a customer-visible Trip Planner chat"
        />
      </Message>
      <TextButton kind="secondary" variant="dark" size="small" onClick={onCreateNewChat}>
        <FormattedMessage
          id="Mb_3wMzOh8DwYXZWmo9n"
          defaultMessage="Create a new, secret chat"
          description="Button that starts a new hidden chat from the customer-visible chat warning banner"
        />
      </TextButton>
      <IconButton kind="tertiary" variant="dark" size="small" onClick={onDismiss} aria-label={closeLabel}>
        <LineTimesIcon />
      </IconButton>
    </Banner>
  )
}

export default CustomerVisibleChatBanner
